import React, { useEffect, useRef, useState } from 'react'
import env from './../../../../environments/environment'
import { formatUrl } from './../../../../shared/formatters/url.formatter'
import './CarrierLogos.component.scss'

const SPEED_PER_SLIDE_SEC = 2.5
const IMAGE_LOAD_TIMEOUT_MS = 5000

const buildImgUrl = cImg => {
  if (!cImg) return ''
  const bucket = env.integrations.firebase.config.storageBucket
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(cImg)}?alt=media`
}

const CarrierLogos = () => {
  const [logos, setLogos] = useState([])
  const [revealed, setRevealed] = useState(false)
  const [scrollVars, setScrollVars] = useState(null)
  const loadedCount = useRef(0)
  const hasRevealed = useRef(false)
  const trackRef = useRef(null)

  const reveal = () => {
    if (hasRevealed.current) return
    hasRevealed.current = true
    // Measure the actual rendered width of the original slides (before
    // duplicates) so the scroll animation distance matches the content.
    requestAnimationFrame(() => {
      if (trackRef.current) {
        const slides = trackRef.current.querySelectorAll('[data-original]')
        let totalWidth = 0
        slides.forEach(s => {
          const style = window.getComputedStyle(s)
          const marginL = parseFloat(style.marginLeft) || 0
          const marginR = parseFloat(style.marginRight) || 0
          totalWidth += s.getBoundingClientRect().width + marginL + marginR
        })
        setScrollVars({
          '--scroll-distance': `-${totalWidth}px`,
          '--scroll-duration': `${slides.length * SPEED_PER_SLIDE_SEC}s`,
        })
      }
      setRevealed(true)
    })
  }

  useEffect(() => {
    // Strip trailing `/api/` — each REST resource is its own Firebase
    // Function (e.g. `/carriers`), not a sub-path of the monolithic `/api`.
    const baseUrl = (env.api.base_url || '').replace(/\/api(\/)?$/, '/')
    fetch(`${baseUrl}carriers/public-logos`)
      .then(r => r.json())
      .then(data => {
        const items = (data.payload || [])
          .filter(c => c.c_img)
          .map(c => ({
            src: buildImgUrl(c.c_img),
            alt: c.c_name || 'Carrier logo',
            href: formatUrl(c.c_website) || null,
          }))
        if (items.length > 0) {
          setLogos(items)
          // Fallback: if images stall, reveal after timeout anyway.
          setTimeout(() => reveal(), IMAGE_LOAD_TIMEOUT_MS)
        }
      })
      .catch(() => {})
  }, [])

  const onImgLoad = () => {
    loadedCount.current += 1
    if (loadedCount.current >= logos.length) reveal()
  }

  const onImgError = () => {
    // Count errors as "loaded" so one broken image doesn't block reveal.
    onImgLoad()
  }

  if (logos.length === 0 && !revealed) return null

  const dupeCount = Math.min(logos.length, 6)

  return (
    <section className={`carrier-logos ${revealed ? 'is-revealed' : ''}`} style={scrollVars || {}}>
      <h2 className='carrier-logos__heading'>
        <span>Access to Top-Rated Insurance Carriers Nationwide*</span>
      </h2>
      <p className='carrier-logos__disclaimer'>Carrier availability varies by state and plan type.</p>
      <hr className='carrier-logos__divider' />
      <div className='carrier-logos__slider'>
        <div className='carrier-logos__track' ref={trackRef}>
          {logos.map((carrier, i) => (
            <div className='carrier-logos__slide' key={i} data-original>
              {carrier.href ? (
                <a href={carrier.href} target='_blank' rel='noopener noreferrer' aria-label={carrier.alt}>
                  <img src={carrier.src} alt={carrier.alt} onLoad={onImgLoad} onError={onImgError} />
                </a>
              ) : (
                <img src={carrier.src} alt={carrier.alt} onLoad={onImgLoad} onError={onImgError} />
              )}
            </div>
          ))}
          {/* Duplicate first N slides for seamless infinite loop */}
          {logos.slice(0, dupeCount).map((carrier, i) => (
            <div className='carrier-logos__slide' key={`dup-${i}`}>
              {carrier.href ? (
                <a
                  href={carrier.href}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={carrier.alt}
                  tabIndex={-1}
                  aria-hidden='true'
                >
                  <img src={carrier.src} alt={carrier.alt} />
                </a>
              ) : (
                <img src={carrier.src} alt={carrier.alt} />
              )}
            </div>
          ))}
        </div>
      </div>
      <hr className='carrier-logos__divider' />
    </section>
  )
}

export default CarrierLogos
